import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowCircleLeft, faPlusCircle, faSpinner, faWandSparkles, faXmark } from "@fortawesome/free-solid-svg-icons";
import { useState } from "react";
import moment from "moment";
import Swal from 'sweetalert2';
import { Calendar, DateObject } from 'react-multi-date-picker';
import { auxInfoEnum, timezones } from "@/common/consts";
import DatePanelRaw from "react-multi-date-picker/plugins/date_panel";

function unwrapCjsDefault<T>(mod: T | { default: T }): T {
  return (mod as any)?.default ?? mod;
}

const DatePanel = unwrapCjsDefault(DatePanelRaw);

const extraOpts: {[index: string]: any} = {
    "first-timer": { text:"If first-timer player", codes: [auxInfoEnum.firstTimer]},
    "help-char-create": { text:"If player needs guidance creating their char", codes: [auxInfoEnum.helpCharCreate]},
    "discord-handle": { text:"Discord handle username", codes: [auxInfoEnum.discordHandle]},
    "veils-lines": { text:"Veils (Uncomfortable subjects) and Lines (PTSD-inducing subjects)" , codes: [auxInfoEnum.veils, auxInfoEnum.lines]},
};

export function CreatePoll() {
    const [ name, setName ] = useState("");
    const [ pass, setPass ] = useState("");
    const [ title, setTitle ] = useState("");
    const [ desc, setDesc ] = useState("");
    const [ timeslotHostLock, setTImeslotHostLock ] = useState(false);
    const [ optsInfo, setOptInfo ]  = useState<string[]>([]);
    const [ loading, setLoading] = useState(false);
    const [ timezone, setTimezone ] = useState(timezones[25]?.value)
    const [ dates, setDates] = useState<DateObject[][]>([]);
    const [ customQuestions, setCustomQuestions] = useState<string[]>([]);

    async function createPoll() {
        if (dates.length == 0) {
            await Swal.fire({
                title: "Need to choose dates",
                icon: "warning",
                theme: "dark",
                focusConfirm: false,
            });
            return;
        }

        const swConf = await Swal.fire({
            title: "Create poll?",
            icon: "question",
            theme: "dark",
            showCancelButton: true,
            focusConfirm: false,
            reverseButtons: true,
            confirmButtonText: "Yes",
            cancelButtonText: "No"
        });
        
        if (!swConf.isConfirmed) {
            return;
        }

        setLoading(true);        
        const res = await fetch("/api/poll/create", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                name,
                pass,
                title,
                desc,
                timezone,
                timeslotHostLock,
                opts: optsInfo.map(key => {
                    if (key in extraOpts) {
                        return extraOpts[key].codes;
                    } else {
                        return([])
                    }
                }).flat(),
                dates: dates.map(e => e.map(e2 => e2.format("YYYY-MM-DD"))),
                customQuestions: customQuestions
            })
        })

        if (res.status == 200) {
            const data = await res.json();
            window.sessionStorage.setItem("OTT-" + data.token, data.ott);
            if (("ott" in data) && ("token" in data)) {
                window.location.href = "/poll/" + data.token;
                return;
            }
        } 

        setLoading(false);
        Swal.fire({
            title: "Server Error",
            theme: 'dark',
            icon: "error",
            text: "sorry for the inconvenience, please let admin know."
        });
    }

    return (
        <div className="flex flex-row justify-center w-screen h-screen">
            { loading && 
                <div className="fixed h-full w-full z-10 flex flex-row">
                    <div className="bg-black opacity-40 fixed h-full w-full z-11"></div>
                    <div className="mx-auto flex flex-col">
                        <div className="my-auto rounded-lg p-8 border border-black bg-dark-secondary z-12 flex flex-col font-bold text-3xl gap-5">
                            <div className="w-full flex flex-row justify-center">
                                <FontAwesomeIcon className="text-[10em] animate-spin" icon={faSpinner} />
                            </div>
                            <div className="w-full text-center">LOADING...</div>
                        </div>
                    </div>
                </div>
            }
            <div className="h-full flex flex-col p-4">
                <form className="bg-dark-secondary rounded-lg p-8 border border-dark-border text-dark-text flex flex-col gap-3 w-[60vw] min-w-[600px]">
                    <div className="flex flex-row justify-between items-end">
                        <button className="dark-button p-1 text-xl rounded border flex items-center justify-center gap-2 font-light flex flex-row gap-2 items-center"
                            onClick={() => {window.location.href = "/"}} type="button"
                        >
                            <FontAwesomeIcon icon={faArrowCircleLeft} />
                            <div className="font-bold">Back</div>
                        </button>
                        <div className="font-bold text-2xl">Create a poll</div>
                    </div>
                    <hr className="h-px my-2 bg-white border-0"/>
                    <div className="flex flex-row gap-1">
                        <div className="flex flex-row gap-2 items-center w-full">
                            <div className="text-nowrap text-xl">Host name:</div>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Host name..."
                                className="dark-input w-full p-2 rounded border font-light"
                                maxLength={20}
                                required
                            />
                        </div>
                        <div className="flex flex-row gap-2 items-center w-full">
                            <div className="text-nowrap text-xl">Host password:</div>
                            <input
                                type="password"
                                value={pass}
                                onChange={(e) => setPass(e.target.value)}
                                placeholder="password"
                                className="dark-input w-full p-2 rounded border font-light"
                                maxLength={40}
                                required
                            />
                        </div>
                    </div>
                    <hr className="h-px my-2 bg-gray-600 border-0"/>
                    <div className="flex flex-row gap-2 items-center w-full">
                        <div className="text-nowrap text-xl">Poll title:</div>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Poll title..."
                            className="dark-input w-full p-2 rounded border font-light"
                            maxLength={40}
                            required
                        />
                    </div>
                    <div className="flex flex-col gap-1 w-full">
                        <div className="text-nowrap text-xl">Description:</div>
                        <textarea
                            value={desc}
                            onChange={(e) => setDesc(e.target.value)}
                            placeholder="Description what kind of event is this (optional)"
                            className="dark-input w-full p-2 rounded border font-light resize-none h-[6em]"
                            maxLength={2000}
                        />
                    </div>
                    <div className="flex flex-row gap-2 w-full">
                        <div className="flex flex-col gap-3">
                            <div className="flex flex-row gap-2 items-center text-xl">
                                <div className="text-nowrap">Open date:</div>
                                <div className="text-nowrap">6 months in advance max</div>
                            </div>
                            <div className="flex flex-row gap-2 items-center text-xl">
                                <Calendar
                                    multiple
                                    range                            
                                    minDate={new DateObject()}
                                    maxDate={new DateObject().add(6, "months")}
                                    value={dates}
                                    onChange={setDates}
                                    plugins={[
                                        <DatePanel position="right"/>
                                    ]}
                                />
                            </div>
                            <div className="text-nowrap text-sm font-bold text-red-500">Poll will be automatically deleted 1 month after the last open date</div>
                            <div className="flex flex-row gap-2 items-center w-full">
                                <div className="text-nowrap text-xl">Timezone:</div>
                                <select 
                                    value={timezone}
                                    onChange={(e) => setTimezone(Number(e.target.value))}
                                    className="font-bold text-xl px-2"
                                >
                                    {timezones.map((tz, idx) => <option className="text-black" key={"opt-" + idx} value={tz.value}>{tz.label}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="flex flex-col gap-3">
                            <div className="flex flex-col gap-1 w-full">
                                <div className="text-nowrap text-xl">Other settings:</div>
                                <ul className="list-none">
                                    <li className="flex flex-row gap-2 items-center cursor-pointer"
                                        onClick={() => setTImeslotHostLock(!timeslotHostLock)}
                                    >
                                        <input className="w-[1.1em] h-[1.1em]" type="checkbox" checked={timeslotHostLock} readOnly/>
                                        <div>Lock timeslot if host is unavailable.</div>
                                    </li>
                                </ul>
                            </div>
                            <div className="flex flex-col gap-1 w-full">
                                <div className="text-nowrap text-xl">Add extra questions:</div>
                                <ul className="list-none">
                                    {Object.entries(extraOpts).map(([key, opt]) => (
                                        <li className="flex flex-row gap-2 items-center cursor-pointer" key={"opts-" + key} 
                                            onClick={() => {
                                                const idx = optsInfo.indexOf(key);
                                                if (idx == -1) {
                                                    setOptInfo([...optsInfo, key]);
                                                } else {
                                                    setOptInfo(optsInfo.filter(e => (e !== key)));
                                                }
                                            }}
                                        >
                                            <input className="w-[1.1em] h-[1.1em]" type="checkbox" checked={optsInfo.includes(key)} readOnly/>
                                            <div>{opt.text}</div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="flex flex-col gap-1 w-full">
                                <div className="flex flex-row items-center gap-2">
                                    <div className="text-nowrap text-xl">Add custom questions (max 3)</div>
                                    {
                                        customQuestions.length < 3 ?
                                        <FontAwesomeIcon icon={faPlusCircle}
                                            className="text-xl font-bold cursor-pointer"
                                            onClick={() => {
                                                Swal.fire({
                                                    title: "+ Custom Question",
                                                    theme: "dark",
                                                    text: 'Add a custom question',
                                                    input: 'text',
                                                    focusConfirm: false,
                                                }).then((res) => {
                                                    if ((res.isConfirmed) && (res.value != '')) {
                                                        setCustomQuestions([...customQuestions, res.value]);
                                                    }
                                                })
                                            }}
                                        /> : null
                                    }                                    
                                </div>
                                
                                <ul className="list-none">
                                    {customQuestions.map((question, idx) => (
                                        <li className="flex flex-row gap-2 items-center cursor-pointer" key={"custom-q-" + idx}>
                                            <FontAwesomeIcon icon={faXmark} className="w-[1.1em] h-[1.1em] font-bold text-xl text-red-500" 
                                                onClick={() => setCustomQuestions(customQuestions.filter((_, idx2) => idx != idx2))}
                                            />
                                            <div>{question}</div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>

                    <button className="dark-button p-1 py-3 text-xl rounded border flex items-center justify-center gap-2 font-light flex flex-row gap-2 items-center"
                        type="submit"
                        onClick={(e) => {
                            if ((name !== "") && (pass !== "" ) && (title !== "")) {
                                e.preventDefault();
                                createPoll();
                            }
                        }}
                    >
                        <FontAwesomeIcon icon={faWandSparkles} />
                        <div className="font-bold">CREATE POLL</div>
                    </button>
                </form>
            </div>
        </div>
    );
}

export default CreatePoll;
